import { useState } from "react";

import {
  LAYOUTS,
  LAYOUT_DEFAULTS,
  getLayout,
  type LayoutId,
  type LayoutOpts,
} from "@/lib/layouts";

interface Props {
  onClose:  () => void;
  onCreate: (name: string, layoutId: LayoutId, opts: LayoutOpts) => Promise<void>;
}

/**
 * "Kitchen Builder" wizard — 1-screen modal that captures project name,
 * layout preset, and primary dimensions. On submit, the parent creates
 * the Project + starter cabinets in a single transaction.
 */
export function NewProjectModal({ onClose, onCreate }: Props) {
  const [name, setName] = useState("Untitled Kitchen");
  const [layout, setLayout] = useState<LayoutId>("straight");
  const [opts, setOpts] = useState<LayoutOpts>(LAYOUT_DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const preset = getLayout(layout);
  const starterCount = preset.starterCabinets(opts).length;

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await onCreate(name.trim(), layout, opts);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.65)",
        display: "grid", placeItems: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 560, maxWidth: "90vw", padding: 24, display: "grid", gap: 16 }}
      >
        <header style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>New project</h2>
          <button className="ghost" style={{ padding: "2px 10px" }} onClick={onClose}>×</button>
        </header>

        <div>
          <label className="label">Project name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={busy}
          />
        </div>

        <div>
          <label className="label">Layout</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {LAYOUTS.map((l) => (
              <button
                key={l.id}
                onClick={() => setLayout(l.id)}
                className="ghost"
                style={{
                  textAlign: "left",
                  padding: 12,
                  border: layout === l.id ? "2px solid var(--accent)" : "1px solid var(--line)",
                  background: layout === l.id ? "var(--bg-elev)" : "transparent",
                  cursor: busy ? "default" : "pointer",
                }}
                disabled={busy}
              >
                <div style={{ fontWeight: 600 }}>{l.label}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{l.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {layout !== "empty" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <DimField
              label="Primary wall"
              value={opts.primaryLength}
              onChange={(v) => setOpts({ ...opts, primaryLength: v })}
            />
            <DimField
              label={layout === "straight" ? "Room depth" : "Secondary wall"}
              value={opts.secondaryLength}
              onChange={(v) => setOpts({ ...opts, secondaryLength: v })}
            />
            <DimField
              label="Ceiling"
              value={opts.ceilingHeight}
              onChange={(v) => setOpts({ ...opts, ceilingHeight: v })}
            />
          </div>
        )}

        <div className="muted" style={{ fontSize: 12 }}>
          {starterCount === 0
            ? "An empty room — you'll add cabinets one by one in the design view."
            : `Will pre-populate ${starterCount} cabinets you can edit afterward.`}
        </div>

        {err && <div className="error">{err}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create project"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DimField({
  label, value, onChange,
}: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="label">{label} <span className="muted" style={{ float: "right" }}>″</span></label>
      <input
        type="number"
        min={36}
        max={480}
        step={6}
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(v);
        }}
      />
    </div>
  );
}
